"use client"

import { useState, useMemo } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Textarea } from "@/components/ui/textarea"

interface Stats {
  characters: number
  charactersNoSpaces: number
  words: number
  sentences: number
  paragraphs: number
  lines: number
  readingTime: string
  speakingTime: string
}

export function WordCounter() {
  const [text, setText] = useState("")

  const stats = useMemo((): Stats => {
    const characters = text.length
    const charactersNoSpaces = text.replace(/\s/g, "").length
    const words = text.trim() ? text.trim().split(/\s+/).length : 0
    const sentences = text.split(/[.!?]+/).filter((s) => s.trim()).length
    const paragraphs = text.split(/\n\n+/).filter((p) => p.trim()).length
    const lines = text.split(/\n/).length

    // Average reading speed: 200-250 words per minute
    const readingMinutes = words / 225
    const readingTime =
      readingMinutes < 1
        ? "< 1 min"
        : `${Math.ceil(readingMinutes)} min`

    // Average speaking speed: 130-150 words per minute
    const speakingMinutes = words / 140
    const speakingTime =
      speakingMinutes < 1
        ? "< 1 min"
        : `${Math.ceil(speakingMinutes)} min`

    return {
      characters,
      charactersNoSpaces,
      words,
      sentences,
      paragraphs,
      lines,
      readingTime,
      speakingTime,
    }
  }, [text])

  const topWords = useMemo(() => {
    if (!text.trim()) return []

    const words = text
      .toLowerCase()
      .replace(/[^\w\s]/g, "")
      .split(/\s+/)
      .filter((w) => w.length > 2)

    const frequency: Record<string, number> = {}
    words.forEach((word) => {
      frequency[word] = (frequency[word] || 0) + 1
    })

    return Object.entries(frequency)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
  }, [text])

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Enter Text</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            placeholder="Type or paste your text here..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="min-h-[200px]"
          />
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-3xl font-bold">{stats.words}</div>
            <div className="text-sm text-muted-foreground">Words</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-3xl font-bold">{stats.characters}</div>
            <div className="text-sm text-muted-foreground">Characters</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-3xl font-bold">{stats.charactersNoSpaces}</div>
            <div className="text-sm text-muted-foreground">Chars (no spaces)</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-3xl font-bold">{stats.sentences}</div>
            <div className="text-sm text-muted-foreground">Sentences</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-3xl font-bold">{stats.paragraphs}</div>
            <div className="text-sm text-muted-foreground">Paragraphs</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-3xl font-bold">{stats.lines}</div>
            <div className="text-sm text-muted-foreground">Lines</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-3xl font-bold">{stats.readingTime}</div>
            <div className="text-sm text-muted-foreground">Reading Time</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-3xl font-bold">{stats.speakingTime}</div>
            <div className="text-sm text-muted-foreground">Speaking Time</div>
          </CardContent>
        </Card>
      </div>

      {topWords.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Most Frequent Words</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {topWords.map(([word, count]) => (
                <div
                  key={word}
                  className="px-3 py-1 bg-muted rounded-full text-sm"
                >
                  <span className="font-medium">{word}</span>
                  <span className="text-muted-foreground ml-1">({count})</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
